import moment from 'moment';
import { TimeDur, timeUnit, tTime } from '../types/time.type';

/**
 * Various functions used for computing time
 */
export class TimeHlp {
  /** https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#description */
  public static readonly MAX_SAFE_TIMESTAMP = 8.64e15;
  /** https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#description */
  public static readonly MIN_SAFE_TIMESTAMP = -8.64e15;

  /** Time durations lengths in seconds */
  public static readonly sec = {
    MINUTE: 60,
    QUARTER: 900,
    HOUR: 3600,
    DAY: 86400,
    WEEK: 604800
  };

  /** Time durations lengths in milliseconds */
  public static readonly ms = {
    MILLISECOND: 1,
    SECOND: 1000,
    MINUTE: 60000,
    QUARTER: 900000,
    HOUR: 3600000,
    DAY: 86400000,
    WEEK: 604800000
  };

  /**
   * Parse a .NET or ISO-8601 time-span value into whole seconds.
   * @param {string | null} value Time-span value
   * @return {number | null} Duration in seconds
   */
  public static parseTimeSpan(value: string | null): number | null {
    if (!value) return null;

    const dotNetMatch = value.trim().match(/^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
    if (dotNetMatch) {
      const [, dayPart, hours, minutes, seconds, fraction = '0'] = dotNetMatch;
      const parsedDays = Number(dayPart ?? 0);
      const parsedHours = Number(hours);
      const parsedMinutes = Number(minutes);
      const parsedSeconds = Number(seconds) + Number(`0.${fraction}`);

      if (parsedHours >= 24 || parsedMinutes >= 60 || parsedSeconds >= 60) return null;
      return TimeHlp.durationToWholeSeconds(parsedDays, parsedHours, parsedMinutes, parsedSeconds);
    }

    const trimmed = value.trim();
    const isoMatch = trimmed.match(
      /^P(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/i
    );
    // ? The optional ISO time section in the regex also accepts a dangling PT designator.
    if (!isoMatch || trimmed.toUpperCase().endsWith('T')) return null;

    const [, dayPart, hourPart, minutePart, secondPart] = isoMatch;
    if (dayPart === undefined && hourPart === undefined && minutePart === undefined && secondPart === undefined) {
      return null;
    }
    if (
      (dayPart?.includes('.') && (hourPart !== undefined || minutePart !== undefined || secondPart !== undefined)) ||
      (hourPart?.includes('.') && (minutePart !== undefined || secondPart !== undefined)) ||
      (minutePart?.includes('.') && secondPart !== undefined)
    ) {
      return null;
    }

    const parsedDays = Number(dayPart ?? 0);
    const parsedHours = Number(hourPart ?? 0);
    const parsedMinutes = Number(minutePart ?? 0);
    const parsedSeconds = Number(secondPart ?? 0);
    if (parsedMinutes >= 60 || parsedSeconds >= 60 || (dayPart !== undefined && parsedHours >= 24)) return null;
    return TimeHlp.durationToWholeSeconds(parsedDays, parsedHours, parsedMinutes, parsedSeconds);
  }

  /**
   * Convert validated duration parts to a safe whole-second value.
   * @param {number} days Days
   * @param {number} hours Hours
   * @param {number} minutes Minutes
   * @param {number} seconds Seconds
   * @return {number | null} Duration in seconds
   */
  private static durationToWholeSeconds(days: number, hours: number, minutes: number, seconds: number): number | null {
    const result = Math.round(days * 86_400 + hours * 3_600 + minutes * 60 + seconds);
    return Number.isSafeInteger(result) ? result : null;
  }

  /**
   * Converts date to timestamp
   * @param {tTime} x date in any format
   * @return {number}
   */
  public static timestamp(x: tTime): number {
    return new Date(x ?? 0).valueOf() ?? 0;
  }

  /**
   * Converts date to timestamp in UTC
   * @param {tTime} x date in any format
   * @return {number}
   */
  public static timestampUTC(x: tTime): number {
    const date = new Date(x ?? 0);
    return date.getTime() + date.getTimezoneOffset() * TimeHlp.ms.MINUTE;
  }

  /**
   * Gets now timestamp in UTC
   * @return {number}
   */
  public static nowUTC(): number {
    return TimeHlp.timestampUTC(Date.now());
  }

  /**
   * Extracts day of week from timeStamp (in more convenient way than raw JS)
   * @param {tTime} x date in any format
   * @return {number} 0 for Monday ... 6 for Sunday
   */
  static dayOfWeek(x: tTime): number {
    return (new Date(x ?? 0).getDay() + 6) % 7;
  }

  /**
   * Get first day of month from date or now with offset
   * @param {tTime}  x
   * @param {number} monthOffset months to subtract
   * @return {number} first day of month
   */
  static startOfMonth(x: tTime, monthOffset = 0): number {
    const date = new Date(x ?? 0);
    return new Date(date.getFullYear(), date.getMonth() - monthOffset, 1).valueOf();
  }

  /**
   * Adds two timestamps
   * @param {tTime} x time in any format
   * @param {tTime} y time in any format
   * @return {number} result timestamp
   */
  public static add(x: tTime, y: tTime): number;

  /**
   * Adds amount of time to given timestamp
   * @param {tTime}    x    time in any format
   * @param {number}   y    amount of unit to add
   * @param {timeUnit} unit of time to add
   * @return {number} result timestamp
   */
  public static add(x: tTime, y: number, unit: timeUnit): number;

  // Implementation
  // eslint-disable-next-line require-jsdoc
  public static add(x: tTime, y: tTime, unit?: timeUnit): number {
    const date = TimeHlp.toJsDate(x);
    if (unit) {
      const yn = y as number;
      switch (unit) {
        case 'MONTH':
          return date.setMonth(date.getMonth() + yn);
        case 'YEAR':
          return date.setFullYear(date.getFullYear() + yn);
        default:
          return TimeHlp.timestamp(x) + yn * TimeHlp.ms[unit];
      }
    } else {
      return TimeHlp.timestamp(x) + TimeHlp.timestamp(y);
    }
  }

  /**
   * Returns timestamp of given date at specific hour
   * @param {tTime}  x    date in any format
   * @param {number} hour
   * @param {number} min
   * @param {number} sec
   * @param {number} ms
   * @return {number}
   */
  public static at(x: tTime, hour: number, min?: number, sec?: number, ms?: number): number {
    return new Date(x ?? 0).setHours(hour, min ?? 0, sec ?? 0, ms ?? 0);
  }

  /**
   * @param {tTime} x
   * @return {number} timestamp of given date at 23:59:59,999
   */
  public static atEnd(x: tTime): number {
    return new Date(x ?? 0).setHours(23, 59, 59, 999);
  }

  /**
   * Returns today's timestamp of given date at specific hour
   * @param {number} hour
   * @param {number} min
   * @param {number} sec
   * @param {number} ms
   * @return {number}
   */
  public static todayAt(hour: number, min?: number, sec?: number, ms?: number): number {
    return new Date().setHours(hour, min ?? 0, sec ?? 0, ms ?? 0);
  }

  /** @return {number} today's timestamp of given date at 23:59:59,999 */
  public static todayAtEnd(): number {
    return new Date().setHours(23, 59, 59, 999);
  }

  /**
   * Check if given timestamp fits in given range (inclusive)
   * @param {tTime}   x
   * @param {TimeDur} range
   * @return {boolean}
   */
  public static inRange(x: tTime, range: TimeDur): boolean;
  /**
   * Check if given timestamp fits in given range (inclusive)
   * @param {tTime} x
   * @param {tTime} from
   * @param {tTime} to
   * @return {boolean}
   */
  public static inRange(x: tTime, from: tTime, to: tTime): boolean;

  // ! implementation
  // eslint-disable-next-line require-jsdoc
  public static inRange(x: tTime, par1: tTime | TimeDur, par2?: tTime): boolean {
    let from: tTime;
    let to: tTime;

    if (par1?.hasOwnProperty('from')) {
      const dur = par1 as TimeDur;
      from = dur.from;
      to = dur.to;
    } else {
      from = TimeHlp.timestamp(par1 as tTime);
      to = TimeHlp.timestamp(par2);
    }

    const timestamp = TimeHlp.timestamp(x);
    return from <= timestamp && timestamp <= to;
  }

  /**
   * Check if given timestamp fits in given range (exclusive)
   * @param {tTime}   x
   * @param {TimeDur} range
   * @return {boolean}
   */
  public static inRangeEx(x: tTime, range: TimeDur): boolean;
  /**
   * Check if given timestamp fits in given range (exclusive)
   * @param {tTime} x
   * @param {tTime} from
   * @param {tTime} to
   * @return {boolean}
   */
  public static inRangeEx(x: tTime, from: tTime, to: tTime): boolean;

  // ! implementation
  // eslint-disable-next-line require-jsdoc
  public static inRangeEx(x: tTime, par1: tTime | TimeDur, par2?: tTime): boolean {
    let from: tTime;
    let to: tTime;

    if (par1?.hasOwnProperty('from')) {
      const dur = par1 as TimeDur;
      from = dur.from;
      to = dur.to;
    } else {
      from = TimeHlp.timestamp(par1 as tTime);
      to = TimeHlp.timestamp(par2);
    }

    const timestamp = TimeHlp.timestamp(x);
    return from < timestamp && timestamp < to;
  }

  /**
   * Checks if dates are equal (if both dates are null, return false)
   * @param {tTime} x
   * @param {tTime} y
   * @return {boolean}
   */
  public static areEqual(x: tTime, y: tTime): boolean {
    if (x == null || y == null) return false;
    return TimeHlp.timestamp(x) == TimeHlp.timestamp(y);
  }

  /**
   * Cast tTime to js Date
   * @param {tTime} x
   * @return {Date} date from x or from 0 when x is null
   */
  public static toJsDate(x: tTime): Date {
    return new Date(x ?? 0);
  }

  /**
   * Cast tTime to js Date or null when value is empty
   * @param {tTime} x
   * @return {Date | null} date from x or null
   */
  public static toJsDateOrNull(x: tTime): Date | null {
    return x != null ? new Date(x) : null;
  }

  /**
   * Checks if two timestamps belong to different days
   * @param {tTime} x
   * @param {tTime} y
   * @return {boolean}
   */
  public static areDifferentDays(x: tTime, y: tTime): boolean {
    if (x == null || y == null) return false;
    return new Date(x).toDateString() !== new Date(y).toDateString();
  }

  /**
   * Time difference in given unit (without rounding)
   * @param {tTime}    x    minuend
   * @param {tTime}    y    subtrahend
   * @param {timeUnit} unit
   * @return {number} (x - y) / unit
   */
  public static difference(x: tTime, y: tTime, unit: timeUnit): number {
    const diff = TimeHlp.timestamp(x) - TimeHlp.timestamp(y);
    switch (unit) {
      case 'MONTH':
        return diff / (30 * TimeHlp.ms.DAY);
      case 'YEAR':
        return diff / (365 * TimeHlp.ms.DAY);
      default:
        return diff / TimeHlp.ms[unit];
    }
  }

  /**
   * Return amount of units between given date and now
   * @param {tTime}            date      null / undefined is replaced by now
   * @param {'floor' | 'ceil'} roundType specifies round function (default 'floor')
   * @param {timeUnit}         unit      specifies unit (default 'DAY')
   * @return {number} units to given date
   */
  public static getCountdown(date: tTime, roundType: 'floor' | 'ceil' = 'floor', unit: timeUnit = 'DAY'): number {
    const roundFunc = roundType == 'floor' ? Math.floor : Math.ceil;
    const now = Date.now();
    return roundFunc(TimeHlp.difference(date ?? now, now, unit));
  }

  /**
   * Format timestamp with moment library
   * @param {tTime}  timestamp
   * @param {string} format    moment format D - day, M - month, Y - year, H - hour 24 format, m - minute, s - second, SSS -
   *                           milliseconds
   * @param {string} fallback  value returned when timestamp is empty
   * @return {string} formatted date, fallback on null
   */
  public static format(timestamp: tTime, format: string, fallback = ''): string {
    return timestamp != null ? moment(timestamp).format(format) : fallback;
  }

  /**
   * timestamp to id for billing and meter reading history
   * @param {number} timestamp
   * @return {string} timestamp in format YYYY_MM_DD_HH_mm_ss
   */
  public static timestampToId(timestamp: number): string {
    return TimeHlp.format(timestamp, 'YYYY_MM_DD_HH_mm_ss');
  }
}
